import React, { useState, useEffect, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { auth } from "./firebase.js";
import { onAuthStateChanged } from "firebase/auth";
import { analytics } from "./firebase.js";
import { logEvent } from "./firebase.js";

// Global CSS imports
import "./styles.css";
import "./styles/design-system.css";

// Components
import NavBar from "./components/NavBar.js";
import { PrivateRoute, VIPRoute, SubscriptionRoute, AccountSetupRoute } from "./components/RouteGuard.jsx";

// Route configuration
import { routeConfig } from "./routes/routeConfig.js";
import { ROUTES_WITHOUT_NAVBAR } from "./routes/routeConstants.js";

// Import the standardized loading component
import LoadingSpinner from "./components/LoadingSpinner.jsx";

const App = () => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const isMobilePhone = window.innerWidth < 768;
  const isLandscape = isMobilePhone && window.screen.orientation && window.screen.orientation.type.includes("landscape");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
    });

    // Analytics tracking
    logEvent(analytics, "page_view", {
      page_path: location.pathname,
      page_title: document.title
    });

    // Scroll to top on route change
    window.scrollTo(0, 0);

    // Mobile orientation lock
    if (
      isMobilePhone &&
      window.screen.orientation &&
      typeof window.screen.orientation.lock === 'function' &&
      typeof window.screen.orientation.unlock === 'function'
    ) {
      window.screen.orientation.lock("portrait").catch((err) => {
        if (process.env.NODE_ENV === "development") {
          console.warn("App.js: Orientation lock failed:", err.message);
        }
      });
    }

    return () => {
      unsubscribe();
      if (
        isMobilePhone &&
        window.screen.orientation &&
        typeof window.screen.orientation.unlock === 'function'
      ) {
        window.screen.orientation.unlock();
      }
    };
  }, [location.pathname, isMobilePhone]);

  // Check if current route should show NavBar
  const shouldShowNavBar = !ROUTES_WITHOUT_NAVBAR.includes(location.pathname);

  // Render route with appropriate guard
  const renderRoute = (route) => {
    const { path, component: Component, private: isPrivate, requireVIP, requireSubscription, requireAccountSetup, suggestDogSetup } = route;

    if (isPrivate) {
      if (requireVIP) {
        return (
          <Route 
            key={path} 
            path={path} 
            element={
              <VIPRoute>
                <Suspense fallback={<LoadingSpinner message="Loading..." fullScreen={true} />}>
                  <Component />
                </Suspense>
              </VIPRoute>
            } 
          />
        );
      } else if (requireSubscription) {
        return (
          <Route 
            key={path} 
            path={path} 
            element={
              <SubscriptionRoute>
                <Suspense fallback={<LoadingSpinner message="Loading..." fullScreen={true} />}>
                  <Component />
                </Suspense>
              </SubscriptionRoute>
            } 
          />
        );
      } else if (requireAccountSetup) {
        return (
          <Route 
            key={path} 
            path={path} 
            element={
              <AccountSetupRoute>
                <Suspense fallback={<LoadingSpinner message="Loading..." fullScreen={true} />}>
                  <Component />
                </Suspense>
              </AccountSetupRoute>
            } 
          />
        );
      } else if (suggestDogSetup) {
        return (
          <Route 
            key={path} 
            path={path} 
            element={
              <PrivateRoute suggestDogSetup={true}>
                <Suspense fallback={<LoadingSpinner message="Loading..." fullScreen={true} />}>
                  <Component />
                </Suspense>
              </PrivateRoute>
            } 
          />
        );
      } else {
        return (
          <Route 
            key={path} 
            path={path} 
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingSpinner message="Loading..." fullScreen={true} />}>
                  <Component />
                </Suspense>
              </PrivateRoute>
            } 
          />
        );
      }
    } else {
      return (
        <Route 
          key={path} 
          path={path} 
          element={
            <Suspense fallback={<LoadingSpinner message="Loading..." fullScreen={true} />}>
              <Component />
            </Suspense>
          } 
        />
      );
    }
  };

  return (
    <div style={styles.appContainer}>
      {isLandscape && (
        <div className="orientation-warning">
          Please rotate your device to portrait mode.
        </div>
      )}
      <div className="main-content">
        {shouldShowNavBar && <NavBar />}
        <div style={styles.pageContainer}>
          <Routes location={location} key={location.pathname}>
            {routeConfig.map(renderRoute)}
          </Routes>
        </div>
      </div>
    </div>
  );
};

const styles = {
  appContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
  },
  pageContainer: {
    flex: 1,
    width: "100%",
    overflow: "hidden",
  },
};

export default App;